import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';

import { useLocation } from 'react-router';

import { useAtomValue, useStore } from 'jotai';

import { useLocationChange } from '@/hooks/useLocationChange';
import { useSmartSearchEnabled } from '@/hooks/useSmartSearchEnabled';

import { debugSearchSessionAtom } from '@/components/developer-menu/state';

import Constants from '@/constants';

import {
  resetSearchSessionAtom,
  searchSessionAtom,
} from './searchSession.state';
import { useSearchSession } from './useSearchSession';

import { useAmplitudeTrack } from '@/amplitude/useAmplitude';

const SEARCH_PATH = `/${Constants.ROUTE_PATH_NAMES.SEARCH_PATH_NAME}`;
const DOC_PATH = `/${Constants.ROUTE_PATH_NAMES.DOCUMENT_PATH_NAME}`;

const ACTIVITY_EVENTS = ['keydown', 'click', 'touchstart'] as const;
const CAPTURE_ACTIVITY_EVENTS = ['scroll'] as const;

const subscribeToVisibility = (callback: () => void): (() => void) => {
  document.addEventListener('visibilitychange', callback);
  return () => document.removeEventListener('visibilitychange', callback);
};

const isSearchRoute = (pathname: string) => pathname.startsWith(SEARCH_PATH);
const isDocRoute = (pathname: string) => pathname.startsWith(DOC_PATH);

export const useSearchSessionTracker = () => {
  const store = useStore();
  const track = useAmplitudeTrack();
  const location = useLocation();
  const atomState = useAtomValue(searchSessionAtom);

  const smartSearchEnabled = useSmartSearchEnabled();

  const session = useSearchSession({
    smartSearchEnabled,
    track,
  });

  const prevCountsRef = useRef({
    searchCount: 0,
    resourceClicks: 0,
    searchQueriesFlushed: 0,
  });

  const flushDeltas = useCallback(() => {
    const current = store.get(searchSessionAtom);
    const searchDelta = current.searchCount - prevCountsRef.current.searchCount;
    const clicksDelta =
      current.resourceClicks - prevCountsRef.current.resourceClicks;

    const queriesStart = prevCountsRef.current.searchQueriesFlushed;
    for (let i = 0; i < searchDelta; i++) {
      const query = current.pendingSearchQueries[queriesStart + i];
      if (query !== undefined) {
        session.noteSearchSubmitted(query);
      } else {
        session.incrementSearchCount();
      }
    }
    prevCountsRef.current.searchQueriesFlushed = queriesStart + searchDelta;
    for (let i = 0; i < clicksDelta; i++) {
      session.incrementResourceClicks();
    }

    prevCountsRef.current = {
      searchCount: current.searchCount,
      resourceClicks: current.resourceClicks,
      searchQueriesFlushed: prevCountsRef.current.searchQueriesFlushed,
    };

    if (current.searchCount === 0) {
      prevCountsRef.current.searchQueriesFlushed = 0;
    }
  }, [store, session]);

  useEffect(() => {
    flushDeltas();
  }, [atomState.searchCount, atomState.resourceClicks, flushDeltas]);

  const journalRef = useRef<{ time: number; event: string; state: string }[]>(
    [],
  );

  const logJournal = useCallback(
    (event: string) => {
      if (!store.get(debugSearchSessionAtom)) return;
      const s = session.getState();
      journalRef.current.push({
        time: Date.now(),
        event,
        state: JSON.stringify(s),
      });
    },
    [store, session],
  );

  const handleFire = useCallback(() => {
    flushDeltas();
    if (store.get(debugSearchSessionAtom)) {
      logJournal('fire');
      const s = session.getState();
      console.debug('[search_time_spent] state:', s);
      journalRef.current = [];
    }
    session.fireAndReset('navigate_away');
    store.set(resetSearchSessionAtom);
  }, [flushDeltas, logJournal, session, store]);

  useLocationChange((curr, prev) => {
    if (!curr?.pathname) return;

    const currIsSearch = isSearchRoute(curr.pathname);
    const currIsDoc = isDocRoute(curr.pathname);
    const prevIsSearch = prev?.pathname ? isSearchRoute(prev.pathname) : false;
    const prevIsDoc = prev?.pathname ? isDocRoute(prev.pathname) : false;

    if (currIsSearch && prevIsDoc) {
      const s = session.getState();
      if (!s.active) {
        session.activate();
        journalRef.current = [];
      }
      session.enterResourceView();
      session.noteSurface('search');
      logJournal('doc→search enterResourceView');
      return;
    }

    if (currIsSearch && !prevIsSearch) {
      const s = session.getState();
      if (s.active) {
        session.enterResourceView();
        session.noteSurface('search');
        logJournal('re-enter search enterResourceView');
      } else {
        session.activate();
        session.enterResourceView();
        session.noteSurface('search');
        journalRef.current = [];
        logJournal('enter search');
      }
      return;
    }

    if (currIsDoc && !prevIsDoc) {
      session.enterResourceView();
      session.noteSurface('doc');
      logJournal('navigate to doc → enterResourceView');
    }

    if (!currIsSearch && !currIsDoc && (prevIsSearch || prevIsDoc)) {
      handleFire();
    }
  });

  const tabHidden = useSyncExternalStore(
    subscribeToVisibility,
    () => document.hidden,
  );

  useEffect(() => {
    const pathname = location.pathname;
    if (!isSearchRoute(pathname) && !isDocRoute(pathname)) {
      return;
    }

    if (tabHidden) {
      session.pauseForHidden();
      return;
    }
    session.resumeFromHidden();
  }, [tabHidden, location.pathname, session]);

  useEffect(() => {
    const pathname = location.pathname;
    if (!isSearchRoute(pathname) && !isDocRoute(pathname)) return;

    const handleActivity = () => {
      session.reportActivity();
    };

    ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });
    CAPTURE_ACTIVITY_EVENTS.forEach(event => {
      document.addEventListener(event, handleActivity, {
        capture: true,
        passive: true,
      });
    });

    return () => {
      ACTIVITY_EVENTS.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      CAPTURE_ACTIVITY_EVENTS.forEach(event => {
        document.removeEventListener(event, handleActivity, { capture: true });
      });
    };
  }, [location.pathname, session]);

  useEffect(() => {
    const handlePageHide = (event: PageTransitionEvent) => {
      flushDeltas();
      if (event.persisted) {
        session.pauseForHidden();
        return;
      }
      session.fireAndReset('pagehide');
    };

    const handlePageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) {
        return;
      }
      const pathname = location.pathname;
      if (!isSearchRoute(pathname) && !isDocRoute(pathname)) {
        return;
      }
      const s = session.getState();
      if (!s.active && s.searchCount > 0) {
        session.activate();
        session.enterResourceView();
      }
      session.resumeFromHidden();
    };

    const handleBeforeUnload = () => {
      flushDeltas();
    };

    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [flushDeltas, location.pathname, session]);
};
